/** A list of runs, as both conversation surfaces render it.
 *
 * Shared because the two had the same columns meaning the same things under
 * different names and in different orders, and every column added to one had to
 * be remembered for the other - the spend column reached them a week apart.
 *
 * Structurally typed: the same rows arrive as `StatefulRun` from a project's own
 * list and as `AgentRun` from the agent-level one, and neither owns the other.
 * What differs per surface is where a row LINKS and whether the site is worth a
 * column, so those are props.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { isDemoMode } from "../demo/demoMode";
import { formatWhen } from "../utils/formatWhen";
import { rowClickHandler } from "../utils/rowClick";
import {
  runParticipantsLabel,
  runStarterLabel,
} from "../utils/runParticipants";
import { waitingLabel } from "../utils/responseCounts";
import {
  passRunTimeLabel,
  runIsWorking,
  runStatusLabel,
  type RunStatusFacts,
} from "../utils/runStatus";
import { RunSpendText } from "./RunView";
import { DeleteConversation } from "./DeleteConversation";
import { CONVERSATION_TYPE_LABEL } from "../utils/conversationTypes";
import styles from "./RunsTable.module.css";
import { Chevron } from "./Chevron";

export interface RunRow extends RunStatusFacts {
  run_id: string;
  kind: string;
  title: string;
  message_count: number;
  spend_usd?: number | null;
  created_at: string;
  updated_at: string;
  /** When a scheduled pass finished, which its run time is read from. */
  pass_ended_at?: string | null;
  started_by_email?: string;
  participants?: string[];
  is_private?: boolean;
  /** Whether the viewer started it, which is who may delete it. */
  is_owner?: boolean;
  /** The sites this run is about, by name and sorted; empty means any site.
   * Resolved server-side, which is where the names live. */
  projects?: string[];
  /** Which AGENTS worked in the run, first-acted first. Distinct from
   * `participants`, which is the people in it. */
  agents_in_run?: string[];
  /** On a project's own list: what this run left waiting on that project. */
  project_waiting?: { emails: number };
  /** A conversation's type: "review" or "other". */
  conversation_type?: string;
  /** A review's comments waiting on the operator's answer. */
  waiting_on_you?: number;
}

type SortKey = "spend" | "activity";

/** Rows in the operator's chosen order.
 *
 * `spend_usd` is null when no ledger row was found, which means "not recorded"
 * rather than "free" - so unpriced rows sort LAST whichever direction is chosen,
 * instead of masquerading as the cheapest.
 */
const sortRuns = (
  runs: readonly RunRow[],
  key: SortKey,
  dir: "asc" | "desc",
): RunRow[] => {
  const sign = dir === "asc" ? 1 : -1;
  return [...runs].sort((a, b) => {
    if (key === "activity") {
      return sign * a.updated_at.localeCompare(b.updated_at);
    }
    const left = a.spend_usd ?? null;
    const right = b.spend_usd ?? null;
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return sign * (left - right);
  });
};

export const RunsTable = ({
  runs,
  href,
  showProjects = false,
  showStatus = false,
  agentLabel,
  subLabel,
  showType = false,
  showRunTime = false,
  allowDelete,
}: {
  runs: readonly RunRow[];
  /** Where a row opens. A scheduled pass belongs to the AGENT even on a
   * project's page, which is why this is the caller's decision. */
  href: (run: RunRow) => string;
  /** The agent-level list spans sites; a project's own is always one site. */
  showProjects?: boolean;
  /** Scheduled passes end and carry a completion decision, so their state is
   * worth a column. A conversation is never closed - it would read "Open" on
   * every row - so a live turn shows as a badge on the title instead. */
  showStatus?: boolean;
  /** Agent key to display name. Without it the raw keys are shown. */
  agentLabel?: (agent: string) => string;
  /** An optional second line under the title, e.g. why a scheduled pass is here. */
  subLabel?: (run: RunRow) => string | null;
  /** Conversations are reviews or other: a column, and a tag where one waits on you. */
  showType?: boolean;
  /** How long each scheduled pass took. A conversation has no end to measure to. */
  showRunTime?: boolean;
  /** A delete control ending each conversation row, enabled where the viewer started
   * it. A scheduled pass never has one. */
  allowDelete: boolean;
}) => {
  // Priced by the list route, in one batch. Hidden in demo mode, as /costs is.
  const showCost = !isDemoMode();
  const navigate = useNavigate();
  // The server already returns newest-activity-first, so this default is the
  // order the page had before it was sortable.
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "activity",
    dir: "desc",
  });
  const ordered = useMemo(
    () => sortRuns(runs, sort.key, sort.dir),
    [runs, sort],
  );
  const nowMs = Date.now();
  const sortable = (key: SortKey, label: string) => (
    <th className={styles.sortable}>
      <button
        type="button"
        className={styles.sortButton}
        onClick={() =>
          setSort((prev) =>
            // Clicking the column already sorted flips it; a new one starts
            // where an operator scanning for "the big ones" wants to start.
            prev.key === key
              ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
              : { key, dir: "desc" },
          )
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span aria-hidden className={styles.sortArrow}>
          {sort.key === key && <Chevron open={sort.dir !== "desc"} />}
        </span>
      </button>
    </th>
  );
  return (
    // A phone scrolls the table sideways, not the whole page.
    <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Run</th>
            {showType && <th>Type</th>}
            {showStatus && <th>Status</th>}
            {showProjects && <th>Projects</th>}
            <th>Started by</th>
            <th>Also in it</th>
            <th>Agents</th>
            <th>Turns</th>
            {showRunTime && <th>Run time</th>}
            {showCost && sortable("spend", "AI spend")}
            {sortable("activity", "Last activity")}
            {allowDelete && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {ordered.map((run) => {
            const scheduled = run.kind === "scheduled";
            // What a finished pass raised or wrote for this site that is still open.
            const waiting =
              scheduled && !runIsWorking(run) && run.project_waiting
                ? waitingLabel(run.project_waiting)
                : "";
            const to = href(run);
            const sub = subLabel?.(run) ?? null;
            return (
              <tr
                key={run.run_id}
                className={
                  (run.waiting_on_you ?? 0) > 0
                    ? `${styles.clickableRow} ${styles.waitingRow}`
                    : styles.clickableRow
                }
                onClick={rowClickHandler(() => navigate(to))}
              >
                <td>
                  {/* The row is the click target; the anchor keeps keyboard use
                    and open-in-new-tab working. */}
                  <Link to={to} onClick={(e) => e.stopPropagation()}>
                    {run.title ||
                      (scheduled ? "Scheduled run" : "Untitled conversation")}
                  </Link>
                  {run.is_private && (
                    <span className={styles.privateTag}>Private</span>
                  )}
                  {(run.waiting_on_you ?? 0) > 0 && (
                    <span className={styles.waitingTag}>Waiting on you</span>
                  )}
                  {/* The one piece of status that still matters on a list with no
                    Status column: a turn is in flight right now. */}
                  {!showStatus && runIsWorking(run) && (
                    <span className={styles.workingTag}>Working…</span>
                  )}
                  {sub && <div className={styles.muted}>{sub}</div>}
                </td>
                {showType && (
                  <td>
                    {CONVERSATION_TYPE_LABEL[
                      run.conversation_type ?? "other"
                    ] ?? "Other"}
                  </td>
                )}
                {showStatus && (
                  <td>
                    {waiting ? (
                      <span>{waiting} still open</span>
                    ) : (
                      runStatusLabel(run)
                    )}
                  </td>
                )}
                {showProjects && (
                  <td>
                    {/* A conversation can be about several sites now, so this is a
                      list rather than a label - and "any site" is a real answer,
                      not a missing one. */}
                    {(run.projects ?? []).length === 0 ? (
                      <span className={styles.muted}>(any site)</span>
                    ) : (
                      <ul className={styles.projectList}>
                        {run.projects?.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                )}
                <td>{runStarterLabel(run)}</td>
                <td>{runParticipantsLabel(run) || "-"}</td>
                <td>
                  {(run.agents_in_run ?? [])
                    .map((a) => agentLabel?.(a) ?? a)
                    .join(", ") || "-"}
                </td>
                <td>{run.message_count}</td>
                {showRunTime && (
                  <td className={styles.nowrap}>
                    {passRunTimeLabel(run, nowMs) ?? "-"}
                  </td>
                )}
                {showCost && (
                  <td
                    className={styles.costCell}
                    // A scheduled run is one bill for every site it worked.
                    title={
                      scheduled
                        ? "The whole run's spend, across every site it worked"
                        : undefined
                    }
                  >
                    <RunSpendText usd={run.spend_usd} />
                  </td>
                )}
                <td>{formatWhen(run.updated_at)}</td>
                {allowDelete && (
                  <td className={styles.actionCell}>
                    <DeleteConversation
                      run={run}
                      trigger="icon"
                      notOwner="disabled"
                      className={styles.actionButton}
                      dangerClassName={styles.actionDanger}
                      // The list refetches once the server has it.
                      onDeleted={() => undefined}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
